import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ExternalLink, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { useAccount } from '../../context/AccountContext';
import {
  GET_PUBLIC_SITE,
  GET_READER_CHAT_MODE,
  GET_TINYCMS_FORMS,
  GET_TINYCMS_PAGE,
} from '../../graphql';
import {
  PUBLISH_TINYCMS_PAGES,
  RUN_TINYCMS_PAGE_COPY_EDIT,
  SAVE_TINYCMS_EDITS,
  SET_TINYCMS_EDIT_PREVIEW,
} from '../../graphql/mutations';
import { useAgentConnected } from './AgentStatus';
import {
  blockRows,
  leftField,
  problemField,
  removeBlock,
  RemovedBlock,
  removedNote,
  shownProblems,
  undoRemove,
} from './blockEdits';
import { PageCopyEdit } from './PageCopyEdit';
import type { Finding } from './shared';
import { linkDestinations } from './linkDestinations';
import { showInkToast } from './InkToast';
import { ago, pageStatus, WorkingPreview } from './CmsPages';
import { DeviceSwitch } from './deviceFrame';
import {
  addressProblem,
  blockLabel,
  blockName,
  blockPrompt,
  DESCRIPTION_MAX,
  editableText,
  footerDuplicates,
  hasEmphasis,
  hasAskPage,
  isWebAddress,
  parseAddress,
  toggleItalics,
  webAddressProblem,
} from './editorText';

/**
 * Website › Pages: edit a page, the header or the footer (designer notes
 * "Website › Pages: editing, and Social › Posts", 2026-10-28, backlog
 * 8ffdec3d). The rail gives way to this 380px panel; the preview beside it
 * follows the typing (the edits ride on this preview's own link —
 * setTinycmsEditPreview — and the working copy changes only on Save draft).
 * Publish saves, runs the account's copy-edit check when it asks for one,
 * and publishes: the person's own edits, so nothing waits in Approvals.
 * The page's heading stays while editing (status, live address, blocks,
 * Show blocks, Desktop/Mobile, Preview ↗), as in the design (8db48ed3).
 */

const LIVE_BLOCKS = gql`
  query TinycmsPageLiveBlocks($accountId: ID!, $id: ID!) {
    tinycmsPageLiveBlocks(accountId: $accountId, id: $id)
  }
`;

export type EditTarget =
  | {
      kind: 'page';
      id: string;
      isHome: boolean;
      landing: boolean;
      mine?: boolean;
      /** A copy edit finding to open on its words (Go to text, under Checks). */
      finding?: Finding;
    }
  | { kind: 'header' }
  | { kind: 'footer' };

type Block = { type: string; [key: string]: any };
type NavLinkRow = { label: string; href: string; [key: string]: any };
type Device = 'desktop' | 'mobile';
/** A row of the Pages rail: enough for the heading kept while editing. */
type PageRowLite = {
  id: string;
  path: string;
  title: string;
  state?: any;
  blockCount?: number;
  updatedAt?: string;
};

interface PageFields {
  title: string;
  address: string;
  description: string;
  blocks: Block[];
}
/** A footer link column: a heading over its links, above the bottom row. */
type FooterColumn = { heading: string; links: NavLinkRow[] };
/** The header's button: an accent pill at the right end of the menu. */
type HeaderButton = { on: boolean; label: string; href: string };
interface PartFields {
  siteName: string;
  footerText: string;
  links: NavLinkRow[];
  columns: FooterColumn[];
  /** Header: show the tagline under the site name (its text is edited in Site). */
  showTagline: boolean;
  button: HeaderButton;
}
type PartProblems = {
  siteName?: string;
  button?: string;
  links: Record<number, string>;
  /** A header link's dropdown links, by the link they sit under. */
  children: Record<number, Record<number, string>>;
  columns: Record<number, { links: Record<number, string>; empty?: string }>;
};

/** The site's limits (siteDocument.js NAV_LIMITS): columns, and links in each. */
const MAX_COLUMNS = 4;
const MAX_COLUMN_LINKS = 10;
/** Links in one header dropdown (NAV_LIMITS.children). */
const MAX_MENU_LINKS = 8;

/** The blocks ADD A BLOCK makes, at the end. */
const NEW_BLOCKS: Array<{ label: string; make: (formId: string) => Block }> = [
  { label: 'Text', make: () => ({ type: 'text', heading: '', body: '' }) },
  { label: 'Article list', make: () => ({ type: 'articles', heading: 'Latest' }) },
  { label: 'Quote', make: () => ({ type: 'quote', text: '', attribution: '' }) },
  {
    label: 'Call to action',
    make: () => ({ type: 'cta', heading: '', button: { label: '', href: '/' } }),
  },
  { label: 'Form', make: (formId) => ({ type: 'form', formId }) },
];
const ADDABLE = new Set(NEW_BLOCKS.map((n) => n.make('').type));

/** What a block still needs before it can be shown, by field. */
export function blockProblems(b: Block): Record<string, string> {
  const out: Record<string, string> = {};
  if (b.type === 'hero' && !String(b.heading || '').trim()) out.heading = 'Add a heading.';
  if (b.type === 'text' && !String(b.heading || '').trim() && !String(b.body || '').trim()) {
    out.body = 'Add a heading or some text.';
  }
  if (b.type === 'quote' && !String(b.text || '').trim()) out.text = 'Add the quote.';
  if (b.type === 'cta') {
    if (!String(b.heading || '').trim()) out.heading = 'Add a heading.';
    if (!String(b.button?.label || '').trim()) out.label = 'Add a button label.';
  }
  if (b.type === 'form' && !b.formId) out.formId = 'Pick a form.';
  return out;
}

const move = <T,>(list: T[], i: number, by: number): T[] => {
  const j = i + by;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** A text block's body for the editor: plain text when it round-trips, else as stored. */
function toEditing(blocks: Block[]): Block[] {
  return blocks.map((b) => {
    if (b.type !== 'text') return b;
    const plain = editableText(b.body);
    return plain === null ? { ...b, _rich: true } : { ...b, body: plain };
  });
}
/** Back to what the server takes: the editor's own marks dropped. */
const toSaving = (blocks: Block[]): Block[] =>
  blocks.map(({ _rich, _new, _left, _reveal, ...b }) =>
    b.type === 'text' && !b.body ? { ...b, body: undefined } : b
  );

/**
 * Publish a member's own saved edits: the page (after the account's copy-edit
 * check, when it asks for one) or the site document (header and footer).
 * { ok: true } or { ok: false, message } in words. Used by the editor and by
 * the "Your edits are saved as a draft" card's Publish changes.
 */
export function usePublishEdits() {
  const { accountId } = useAccount();
  const [copyEdit] = useMutation(RUN_TINYCMS_PAGE_COPY_EDIT);
  const [publish] = useMutation(PUBLISH_TINYCMS_PAGES);
  return async (
    what:
      | { pageId: string; workingHash: string; copyEditRequired?: boolean }
      | { siteHash: string },
    onStep?: (step: 'checking' | 'publishing') => void
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    const variables =
      'pageId' in what
        ? { accountId, pages: [{ pageId: what.pageId, contentHash: what.workingHash }] }
        : { accountId, pages: [], site: { contentHash: what.siteHash } };
    if ('pageId' in what && what.copyEditRequired) {
      onStep?.('checking');
      await copyEdit({ variables: { accountId, id: what.pageId } });
    }
    onStep?.('publishing');
    const { data } = await publish({ variables });
    const result = data?.publishTinycmsPages;
    if (result && result.ok === false) {
      const first = (result.findings || []).find((f: any) => f.severity === 'blocking');
      return {
        ok: false,
        message: first?.label || first?.message || result.message || 'A check didn’t pass',
      };
    }
    return { ok: true };
  };
}

export function PageEditor({
  target,
  site,
  pages,
  onClose,
  onSaved,
}: {
  target: EditTarget;
  site: any;
  pages: PageRowLite[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { accountId } = useAccount();
  const agentConnected = useAgentConnected();
  const isPage = target.kind === 'page';
  const pageId = target.kind === 'page' ? target.id : null;
  const homeSlug: string | null = site?.working?.layout?.homePage || null;

  const { data: pageData } = useQuery(GET_TINYCMS_PAGE, {
    variables: { accountId, id: pageId },
    skip: !accountId || !pageId,
    fetchPolicy: 'network-only',
  });
  const { data: liveData } = useQuery(LIVE_BLOCKS, {
    variables: { accountId, id: pageId },
    skip: !accountId || !pageId,
    fetchPolicy: 'network-only',
  });
  const { data: formsData } = useQuery(GET_TINYCMS_FORMS, {
    variables: { accountId },
    skip: !accountId || !isPage,
  });
  // The header's site name, when the site has none of its own: the account's
  // Public Site title (what readers see; publicSite.js siteIdentity).
  const { data: accountData } = useQuery(GET_PUBLIC_SITE, {
    variables: { accountId },
    skip: !accountId || target.kind !== 'header',
  });
  const { data: chatData } = useQuery(GET_READER_CHAT_MODE, {
    variables: { accountId },
    skip: !accountId || target.kind !== 'header',
  });
  const askInMenu = hasAskPage(chatData?.readerChat);
  const forms: Array<{ id: string; name: string }> = formsData?.tinycmsForms || [];
  const page = pageData?.tinycmsPage;

  // What was there when the editor opened, and what it is now.
  const [original, setOriginal] = useState<PageFields | PartFields | null>(null);
  const [fields, setFields] = useState<PageFields | PartFields | null>(null);
  useEffect(() => {
    if (original) return;
    if (isPage && page) {
      const c = page.content || {};
      const start: PageFields = {
        title: c.title || '',
        address: target.kind === 'page' && target.isHome ? '/' : page.path,
        description: c.seo?.metaDescription || '',
        blocks: toEditing(c.blocks || []),
      };
      setOriginal(start);
      setFields(start);
    } else if (!isPage && site && (target.kind !== 'header' || accountData)) {
      const nav = site.working?.navigation || {};
      const start: PartFields = {
        siteName:
          site.working?.identity?.title ||
          accountData?.account?.publicTitle ||
          accountData?.account?.name ||
          '',
        footerText: nav.footerText || '',
        links: ((target.kind === 'header' ? nav.header : nav.footer) || []).map(
          (l: NavLinkRow) => ({ ...l, href: l.href || '' })
        ),
        columns:
          target.kind === 'footer'
            ? (nav.footerColumns || []).map((c: { heading?: string; links?: NavLinkRow[] }) => ({
                heading: c.heading || '',
                links: (c.links || []).map((l) => ({ ...l, href: l.href || '' })),
              }))
            : [],
        showTagline: site.working?.layout?.showTagline !== false,
        button: nav.cta
          ? { on: true, label: nav.cta.label || '', href: nav.cta.href || '' }
          : { on: false, label: '', href: '/' },
      };
      setOriginal(start);
      setFields(start);
    }
  }, [isPage, page, site, original, target, accountData]);

  const dirty = Boolean(fields && original && !same(fields, original));
  const [picked, setPicked] = useState<number | null>(null);
  // Blocks taken out in this edit, each with an Undo in its place until a save.
  const [removed, setRemoved] = useState<RemovedBlock[]>([]);
  const removedCount = useRef(0);
  // Where the keyboard goes once the next render is on screen.
  const focusNext = useRef<{ selector: string; scroll?: boolean } | null>(null);
  useEffect(() => {
    if (!focusNext.current) return;
    const { selector, scroll } = focusNext.current;
    focusNext.current = null;
    document.querySelector<HTMLElement>(selector)?.focus({ preventScroll: !scroll });
  });
  const [device, setDevice] = useState<Device>('desktop');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState<'' | 'saving' | 'checking' | 'publishing'>('');
  const [error, setError] = useState('');
  const [previewNote, setPreviewNote] = useState('');
  const [showBlocks, setShowBlocks] = useState(false);
  const row = pageId ? pages.find((p) => p.id === pageId) || null : null;
  const siteUrl: string | null = site?.siteUrl || null;
  const siteHost = siteUrl ? siteUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '') : null;

  // Where links can go: pages, sections, the feed and the privacy page.
  const destinations = useMemo(
    () => linkDestinations(pages, site?.working?.collections || [], homeSlug),
    [pages, site, homeSlug]
  );

  // Problems that keep a save (and the preview) from taking the edits.
  const taken = useMemo(
    () => new Set(pages.filter((p) => p.id !== pageId).map((p) => p.path)),
    [pages, pageId]
  );
  const problems = useMemo(() => {
    const out: {
      title?: string;
      address?: string;
      siteName?: string;
      button?: string;
      links: Record<number, string>;
      children: PartProblems['children'];
      columns: PartProblems['columns'];
      blocks: Record<number, Record<string, string>>;
      /** What's on screen of those: a new block stays quiet until it's left (blockEdits). */
      shownBlocks: Record<number, Record<string, string>>;
    } = { links: {}, children: {}, columns: {}, blocks: {}, shownBlocks: {} };
    if (!fields) return out;
    if ('blocks' in fields) {
      if (!fields.title.trim()) out.title = 'Add a title.';
      const home = target.kind === 'page' && target.isHome;
      const addr = home ? '' : addressProblem(fields.address, taken);
      if (addr) out.address = addr;
      fields.blocks.forEach((b, i) => {
        const p = blockProblems(b);
        if (Object.keys(p).length) out.blocks[i] = p;
        const shown = shownProblems(b, p);
        if (Object.keys(shown).length) out.shownBlocks[i] = shown;
      });
    } else {
      if (target.kind === 'header' && !fields.siteName.trim()) out.siteName = 'Add a site name.';
      fields.links.forEach((l, i) => {
        const children: NavLinkRow[] = Array.isArray(l.children) ? l.children : [];
        if (!l.label.trim()) out.links[i] = 'Every link needs a label.';
        else if (!l.href && !children.length) {
          out.links[i] = 'Pick where it goes, or add links under it.';
        } else if (webAddressProblem(l.href)) out.links[i] = webAddressProblem(l.href);
        const under: Record<number, string> = {};
        children.forEach((c, j) => {
          if (!c.label.trim()) under[j] = 'Every link needs a label.';
          else if (webAddressProblem(c.href)) under[j] = webAddressProblem(c.href);
        });
        if (Object.keys(under).length) out.children[i] = under;
      });
      if (target.kind === 'header' && fields.button.on) {
        if (!fields.button.label.trim()) out.button = 'Give the button a label, or turn it off.';
        else if (!fields.button.href) out.button = 'Pick where the button goes.';
        else if (webAddressProblem(fields.button.href)) {
          out.button = webAddressProblem(fields.button.href);
        }
      }
      fields.columns.forEach((c, i) => {
        const col: PartProblems['columns'][number] = { links: {} };
        c.links.forEach((l, j) => {
          if (!l.label.trim()) col.links[j] = 'Every link needs a label.';
          else if (webAddressProblem(l.href)) col.links[j] = webAddressProblem(l.href);
        });
        if (!c.links.length) col.empty = 'Add a link to this column, or remove the column.';
        if (col.empty || Object.keys(col.links).length) out.columns[i] = col;
      });
    }
    return out;
  }, [fields, taken, target]);
  const validBesideBlocks =
    !problems.title &&
    !problems.address &&
    !problems.siteName &&
    !problems.button &&
    !Object.keys(problems.links).length &&
    !Object.keys(problems.children).length &&
    !Object.keys(problems.columns).length;
  const valid = validBesideBlocks && !Object.keys(problems.blocks).length;
  // Save and Publish wait on a problem the person can see. One still hidden in a
  // new block leaves them pressable, and pressing shows it (revealProblems).
  const canPress = validBesideBlocks && !Object.keys(problems.shownBlocks).length;

  /** What the server is sent: the page's fields, or the site's. */
  const payload = useMemo(() => {
    if (!fields) return null;
    if ('blocks' in fields) {
      const home = target.kind === 'page' && target.isHome;
      const addr = home ? null : parseAddress(fields.address);
      return {
        page: {
          title: fields.title.trim(),
          ...(addr ? { slug: addr.slug, parent: addr.parent } : {}),
          metaDescription: fields.description.trim(),
          blocks: toSaving(fields.blocks),
        },
      };
    }
    // A header link with its dropdown emptied is a plain link again.
    const links = fields.links.map(({ children, ...l }) => ({
      ...l,
      label: l.label.trim(),
      ...(Array.isArray(children) && children.length
        ? {
            children: children.map((c: NavLinkRow) => ({
              ...c,
              label: c.label.trim(),
              ...(typeof c.description === 'string' ? { description: c.description.trim() } : {}),
            })),
          }
        : {}),
    }));
    // The name only when it changed: an unchanged one may be the account's title.
    const renamed = original && 'siteName' in original && fields.siteName !== original.siteName;
    // The button and tagline switch only when they changed, so opening and
    // saving the header never rewrites settings the person didn't touch.
    const was = original && 'button' in original ? original : null;
    const button = fields.button;
    const buttonChanged = !was || !same(button, was.button);
    const taglineChanged = !was || fields.showTagline !== was.showTagline;
    return {
      site:
        target.kind === 'header'
          ? {
              ...(renamed ? { siteName: fields.siteName.trim() } : {}),
              header: links,
              ...(buttonChanged
                ? {
                    cta: button.on
                      ? { label: button.label.trim(), href: button.href.trim() }
                      : null,
                  }
                : {}),
              ...(taglineChanged ? { showTagline: fields.showTagline } : {}),
            }
          : {
              footerText: fields.footerText.trim(),
              footer: links,
              footerColumns: fields.columns.map((c) => ({
                ...(c.heading.trim() ? { heading: c.heading.trim() } : {}),
                links: c.links.map((l) => ({ ...l, label: l.label.trim() })),
              })),
            },
    };
  }, [fields, original, target]);

  // The preview follows the typing, a moment after it stops.
  const previewRef = useRef<{
    token: string;
    href: string;
    post: (msg: object) => void;
  } | null>(null);
  const [previewHref, setPreviewHref] = useState<string | null>(null);
  const [setPreview] = useMutation(SET_TINYCMS_EDIT_PREVIEW);
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  useEffect(() => {
    if (!dirty || !valid || !payload || !previewToken || !accountId) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await setPreview({
          variables: { accountId, token: previewToken, pageId, ...payload },
        });
        const r = data?.setTinycmsEditPreview;
        if (r && r.ok === false) {
          setPreviewNote('The preview shows your edits once you save them.');
          return;
        }
        setPreviewNote('');
        previewRef.current?.post({ type: 'tinycms:preview-refresh' });
      } catch (err) {
        setPreviewNote(
          err instanceof Error ? err.message : 'The preview couldn’t follow that edit.'
        );
      }
    }, 400);
    return () => clearTimeout(t);
  }, [payload, dirty, valid, previewToken, accountId, pageId, setPreview]);

  // Outline the block being edited in the preview ("Hero · editing").
  // A copy edit finding also marks its words there.
  const quoteRef = useRef<string | null>(null);
  const markInPreview = (i: number | null, blocks: Block[]) =>
    previewRef.current?.post({
      type: 'tinycms:edit-focus',
      block: i,
      label: i === null || !blocks[i] ? '' : `${blockName(blocks[i].type)} · editing`,
      quote: quoteRef.current,
    });
  const pick = (i: number | null) => {
    setPicked(i);
    if (fields && 'blocks' in fields) markInPreview(i, fields.blocks);
  };

  const markFinding = (i: number | null, quote: string | null) => {
    quoteRef.current = quote;
    if (quote) {
      pick(i);
    } else if (fields && 'blocks' in fields) {
      markInPreview(picked, fields.blocks);
    }
  };

  const removeAt = (i: number) => {
    if (!fields || !('blocks' in fields)) return;
    removedCount.current += 1;
    const key = `r${removedCount.current}`;
    const next = removeBlock(fields.blocks, removed, i, key);
    setFields({ ...fields, blocks: next.blocks });
    setRemoved(next.removed);
    setPicked(null);
    markInPreview(null, next.blocks);
    focusNext.current = { selector: `[data-undo-k="${key}"]` };
  };
  const undoAt = (key: string) => {
    if (!fields || !('blocks' in fields)) return;
    const next = undoRemove(fields.blocks, removed, key);
    if (!next) return;
    setFields({ ...fields, blocks: next.blocks });
    setRemoved(next.removed);
    setPicked(next.at);
    markInPreview(next.at, next.blocks);
    focusNext.current = { selector: `[data-block-remove="${next.at}"]` };
  };

  const setPage = (patch: Partial<PageFields>) =>
    setFields((f) => (f && 'blocks' in f ? { ...f, ...patch } : f));
  const setPart = (patch: Partial<PartFields>) =>
    setFields((f) => (f && 'links' in f ? { ...f, ...patch } : f));
  const setBlock = (i: number, patch: Partial<Block>) =>
    setFields((f) =>
      f && 'blocks' in f
        ? { ...f, blocks: f.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) }
        : f
    );

  // Save draft / Publish.
  const [save] = useMutation(SAVE_TINYCMS_EDITS);
  const publishEdits = usePublishEdits();
  const doSave = async () => {
    const { data } = await save({ variables: { accountId, pageId, ...payload } });
    return data?.saveTinycmsEdits as { workingHash: string | null; siteHash: string | null };
  };
  const title =
    target.kind === 'page'
      ? (fields && 'title' in fields && fields.title) || page?.title || 'This page'
      : target.kind === 'header'
        ? 'The header'
        : 'The footer';
  /** Save or Publish met a new block's hidden problems: show them, at the first. */
  const revealProblems = () => {
    if (!fields || !('blocks' in fields)) return;
    const first = Number(Object.keys(problems.blocks)[0]);
    const key = problemField(problems.blocks[first] || {});
    setFields({
      ...fields,
      blocks: fields.blocks.map((b, i) => (problems.blocks[i] ? { ...b, _reveal: true } : b)),
    });
    if (key) {
      focusNext.current = {
        selector: `[data-ce-block="${first}"] [data-ce-key="${key}"]`,
        scroll: true,
      };
    }
  };
  const onSaveDraft = async () => {
    if (!valid && canPress) return revealProblems();
    if (!valid || !dirty) return;
    setBusy('saving');
    setError('');
    try {
      await doSave();
      showInkToast('Saved as a draft. Readers still see the live version until you publish.');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    } finally {
      setBusy('');
    }
  };
  const onPublish = async () => {
    if (!valid && canPress) return revealProblems();
    if (!valid || !dirty) return;
    setBusy('saving');
    setError('');
    try {
      const saved = await doSave();
      const r =
        pageId && saved.workingHash
          ? await publishEdits(
              {
                pageId,
                workingHash: saved.workingHash,
                copyEditRequired: Boolean(page?.state?.copyEdit?.required),
              },
              setBusy
            )
          : saved.siteHash
            ? await publishEdits({ siteHash: saved.siteHash }, setBusy)
            : ({ ok: false, message: 'Nothing was saved' } as const);
      onSaved();
      if (!r.ok) {
        setError(
          `Saved as a draft, but it can’t go live yet: ${r.message}. See Checks, publishing and history under the preview.`
        );
        setOriginal(fields);
        // Saved: what was removed is gone now, so there's nothing left to undo.
        setRemoved([]);
        return;
      }
      showInkToast(`${title} is live with your edits.`);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t publish. Try again.');
    } finally {
      setBusy('');
    }
  };
  const cancel = () => (dirty ? setAsking(true) : onClose());

  // A block the agent proposed: not in the live version (or the page was never published).
  const live: Block[] | null = liveData?.tinycmsPageLiveBlocks ?? null;
  const fromAgent = (b: Block) =>
    Boolean(agentConnected) &&
    !b._new &&
    target.kind === 'page' &&
    !target.mine &&
    !page?.state?.ownEdit &&
    page?.state?.hasUnpublishedChanges &&
    !(live || []).some((l) => same(toEditing([l])[0], b));

  const eyebrow =
    target.kind === 'page'
      ? target.landing
        ? 'EDITING · LANDING PAGE'
        : 'EDITING · PAGE'
      : 'EDITING · ON EVERY PAGE';
  const draftPage = isPage && !page?.state?.live;
  const loading = !fields;

  return (
    <div className="pg pe">
      <aside className="pe-panel" aria-label="Editor">
        <div className="pe-head">
          <div style={{ minWidth: 0 }}>
            <div className="pe-eyebrow">{eyebrow}</div>
            <div className="pe-title">
              {target.kind === 'page'
                ? page?.title || 'Page'
                : target.kind === 'header'
                  ? 'Header'
                  : 'Footer'}
            </div>
          </div>
          <span className="pe-state" data-dirty={dirty ? 'true' : undefined}>
            {dirty ? 'Unsaved changes' : 'No changes yet'}
          </span>
        </div>

        <div className="pe-body">
          {loading ? (
            <p className="ink-muted">Loading…</p>
          ) : fields && 'blocks' in fields ? (
            <>
              {pageId && payload?.page ? (
                <PageCopyEdit
                  pageId={pageId}
                  page={fields}
                  draft={{
                    title: payload.page.title,
                    metaDescription: payload.page.metaDescription,
                    blocks: payload.page.blocks,
                  }}
                  canRun={valid}
                  saved={page?.state?.copyEdit}
                  opening={target.kind === 'page' ? target.finding : null}
                  onPage={(next) => setFields(next as PageFields)}
                  onMark={markFinding}
                />
              ) : null}
              <PageFieldsForm
                fields={fields}
                problems={problems}
                isHome={target.kind === 'page' && target.isHome}
                siteHost={siteHost}
                wasLive={Boolean(page?.state?.live)}
                liveAddress={page?.state?.live?.path || null}
                forms={forms}
                destinations={destinations}
                picked={picked}
                onPick={pick}
                onPage={setPage}
                onBlock={setBlock}
                fromAgent={fromAgent}
                removed={removed}
                onRemove={removeAt}
                onUndo={undoAt}
              />
            </>
          ) : fields && 'links' in fields ? (
            <PartFieldsForm
              part={target.kind === 'header' ? 'header' : 'footer'}
              fields={fields}
              problems={problems}
              destinations={destinations}
              mega={site?.working?.layout?.menu === 'mega'}
              tagline={
                site?.working?.identity?.tagline ?? accountData?.account?.publicTagline ?? ''
              }
              askInMenu={askInMenu}
              onPart={setPart}
            />
          ) : null}
        </div>

        <div className="pe-foot">
          {error ? (
            <p className="pe-error" role="alert">
              {error}
            </p>
          ) : null}
          {asking ? (
            <div className="pe-ask" role="alertdialog" aria-label="Discard your edits?">
              <span>Discard your edits?</span>
              <span className="pe-ask-buttons">
                <button type="button" className="ink-btn" onClick={() => setAsking(false)}>
                  Keep editing
                </button>
                <button type="button" className="ink-btn pe-danger" onClick={onClose}>
                  Discard
                </button>
              </span>
            </div>
          ) : (
            <div className="pe-buttons">
              <button type="button" className="ink-btn" onClick={cancel} disabled={Boolean(busy)}>
                Cancel
              </button>
              <span className="pe-buttons-right">
                <button
                  type="button"
                  className="ink-btn"
                  disabled={!dirty || !canPress || Boolean(busy)}
                  onClick={() => void onSaveDraft()}
                >
                  {busy === 'saving' && !error ? 'Saving…' : 'Save draft'}
                </button>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={!dirty || !canPress || Boolean(busy)}
                  onClick={() => void onPublish()}
                >
                  {busy === 'checking'
                    ? 'Checking…'
                    : busy === 'publishing'
                      ? 'Publishing…'
                      : draftPage
                        ? 'Publish page'
                        : 'Publish'}
                </button>
              </span>
            </div>
          )}
        </div>
      </aside>

      <div className="pg-main">
        <div className="pg-head">
          <div style={{ minWidth: 0 }}>
            <div className="pg-title">{title}</div>
            <EditingMeta
              row={row}
              isHome={target.kind === 'page' && target.isHome}
              blocks={fields && 'blocks' in fields ? fields.blocks.length : null}
              siteUrl={siteUrl}
              partPending={
                target.kind === 'page' ? null : Boolean(site?.state?.hasUnpublishedChanges)
              }
            />
            <div className="pe-preview-line" data-warn={previewNote ? 'true' : undefined}>
              {previewNote || 'Live preview of your edits. Readers see them after you publish.'}
            </div>
          </div>
          <div className="pg-tools">
            {isPage ? (
              <button
                type="button"
                className="pg-check"
                aria-pressed={showBlocks}
                onClick={() => setShowBlocks((v) => !v)}
              >
                <span className="pg-box" data-on={showBlocks ? 'true' : undefined} />
                Show blocks
              </button>
            ) : null}
            <DeviceSwitch device={device} onDevice={setDevice} />
            {previewHref ? (
              // Full size on the editor's own preview link, so it shows the edits as typed.
              <Button variant="outline" size="sm" asChild>
                <a href={previewHref} target="_blank" rel="noreferrer">
                  <Eye /> Preview <ExternalLink className="size-3" />
                </a>
              </Button>
            ) : null}
          </div>
        </div>
        <WorkingPreview
          pageId={pageId || undefined}
          path={isPage ? (target.kind === 'page' && target.isHome ? '/' : page?.path) : '/'}
          device={device}
          outline={target.kind === 'page' ? (showBlocks ? 'blocks' : null) : target.kind}
          onReady={(p) => {
            previewRef.current = p;
            setPreviewToken(p.token);
            setPreviewHref(p.href);
          }}
          onFrameLoad={() => {
            // A reloaded frame (Show blocks, Desktop/Mobile) forgets the mark.
            if (picked !== null && fields && 'blocks' in fields)
              markInPreview(picked, fields.blocks);
          }}
        />
      </div>
    </div>
  );
}

function Field({
  label,
  note,
  problem,
  children,
}: {
  label: string;
  note?: React.ReactNode;
  problem?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="pe-field">
      <span className="pe-label">{label}</span>
      {children}
      {problem ? (
        <span className="pe-problem">{problem}</span>
      ) : note ? (
        <span className="pe-note">{note}</span>
      ) : null}
    </label>
  );
}

function GoesTo({
  value,
  onChange,
  destinations,
  label = 'Goes to',
  parent = false,
}: {
  value: string;
  onChange: (href: string) => void;
  destinations: Array<{ group: string; items: Array<{ href: string; label: string }> }>;
  label?: string;
  /** A dropdown parent: with no address of its own it opens its menu (b21412e4). */
  parent?: boolean;
}) {
  // "Web address…" is the last choice: it shows an https:// field under the
  // row (446d9c6d). A link that already leaves the site opens in it.
  // Rows are keyed by position, so the value decides: the state only keeps the
  // field open while a half-typed address doesn't start with http yet.
  const [typing, setTyping] = useState(false);
  const known = destinations.some((g) => g.items.some((i) => i.href === value));
  const web = isWebAddress(value) || (typing && !known);
  const none = parent ? 'Opens a menu' : 'Pick where it goes';
  return (
    <>
      <select
        className="ink-site-input pe-select"
        aria-label={label}
        value={web ? WEB_ADDRESS : value}
        onChange={(e) => {
          const next = e.target.value;
          setTyping(next === WEB_ADDRESS);
          if (next === WEB_ADDRESS) onChange(isWebAddress(value) ? value : 'https://');
          else onChange(next);
        }}
      >
        {!known && !web ? <option value={value}>{value || none}</option> : null}
        {destinations.map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.items.map((i) => (
              <option key={`${g.group}${i.href}`} value={i.href}>
                {i.label}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={WEB_ADDRESS}>Web address…</option>
      </select>
      {web ? (
        <input
          className="ink-site-input"
          type="url"
          inputMode="url"
          aria-label="Web address"
          placeholder="https://example.com"
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
        />
      ) : null}
    </>
  );
}

const WEB_ADDRESS = '__web__';

/** A labelled on/off switch with a note under it (Tagline in header, the button). */
function SwitchRow({
  label,
  on,
  note,
  onChange,
}: {
  label: string;
  on: boolean;
  note?: React.ReactNode;
  onChange: (on: boolean) => void;
}) {
  return (
    <div className="pe-switch-row">
      <span className="pe-switch-text">
        <span className="pe-label">{label}</span>
        {note ? <span className="pe-note">{note}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        className="pe-switch"
        onClick={() => onChange(!on)}
      >
        <span className="ink-switch" data-on={on} />
      </button>
    </div>
  );
}

/**
 * A block the editor has no fields for (Features, Columns, Custom HTML…):
 * the agent edits it; here it can be moved or removed. Ask your agent opens a
 * ready prompt naming the page and the block, with Copy prompt (Round 34 B).
 */
function AgentEditsBlock({ prompt }: { prompt: string }) {
  const [open, setOpen] = useState(false);
  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(prompt);
      showInkToast('Prompt copied. Paste it into your agent and add what to change.');
      setOpen(false);
    } catch {
      showInkToast('Couldn’t copy. Select the prompt and copy it yourself.');
    }
  };
  return (
    <div className="pe-agent-edits">
      <p className="pe-note">Your agent edits this block. You can move or remove it here.</p>
      <button
        type="button"
        className="ink-btn pe-agent-ask"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        Ask your agent
      </button>
      {open ? (
        <div className="pe-prompt" onClick={(e) => e.stopPropagation()}>
          <span className="pe-prompt-label">PROMPT FOR YOUR AGENT</span>
          <p className="pe-prompt-text">{prompt}</p>
          <button type="button" className="ink-btn ink-btn-primary" onClick={copy}>
            Copy prompt
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A block heading. *Stars* around words set them in italics on the site
 * (smagent-sites lib/emphasis.tsx); the I button and ⌘I/Ctrl+I put the
 * selection in or out of stars, and the hint says so while the field is in
 * use or already has italics (backlog 7f8b4dc6).
 */
function HeadingField({
  value,
  problem,
  onChange,
}: {
  value: string;
  problem?: string;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const italics = () => {
    const el = ref.current;
    if (!el) return;
    const next = toggleItalics(
      value,
      el.selectionStart ?? value.length,
      el.selectionEnd ?? value.length
    );
    onChange(next.value);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.start, next.end);
    });
  };
  return (
    <Field
      label="Heading"
      problem={problem}
      note={
        focused || hasEmphasis(value)
          ? 'Wrap words in *stars* for italics, or select them and press I.'
          : undefined
      }
    >
      <span className="pe-input-row">
        <input
          ref={ref}
          className="ink-site-input"
          data-ce-key="heading"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'i') {
              e.preventDefault();
              italics();
            }
          }}
        />
        <button
          type="button"
          className="pe-italic"
          aria-label="Italics"
          title="Italics (⌘I)"
          // Keep the focus and the selection in the heading.
          onMouseDown={(e) => e.preventDefault()}
          onClick={italics}
        >
          I
        </button>
      </span>
    </Field>
  );
}

function PageFieldsForm({
  fields,
  problems,
  isHome,
  siteHost,
  wasLive,
  liveAddress,
  forms,
  destinations,
  picked,
  removed,
  onRemove,
  onUndo,
  onPick,
  onPage,
  onBlock,
  fromAgent,
}: {
  fields: PageFields;
  problems: {
    title?: string;
    address?: string;
    shownBlocks: Record<number, Record<string, string>>;
  };
  isHome: boolean;
  /** "kt-newsroom.tinycms.ai": the address line under the field (3407cb7d). */
  siteHost: string | null;
  wasLive: boolean;
  liveAddress: string | null;
  forms: Array<{ id: string; name: string }>;
  destinations: Array<{ group: string; items: Array<{ href: string; label: string }> }>;
  picked: number | null;
  removed: RemovedBlock[];
  onRemove: (i: number) => void;
  onUndo: (key: string) => void;
  onPick: (i: number | null) => void;
  onPage: (patch: Partial<PageFields>) => void;
  onBlock: (i: number, patch: Partial<Block>) => void;
  fromAgent: (b: Block) => boolean;
}) {
  const moved = wasLive && liveAddress && !isHome && fields.address !== liveAddress;
  const fullAddress = siteHost ? `${siteHost}${fields.address.trim()}` : null;
  const setBlocks = (blocks: Block[]) => onPage({ blocks });
  return (
    <>
      <Field label="Title" problem={problems.title}>
        <input
          className="ink-site-input"
          data-ce-field="title"
          value={fields.title}
          onChange={(e) => onPage({ title: e.target.value })}
        />
      </Field>
      <Field
        label="Address"
        problem={problems.address}
        note={
          isHome
            ? 'Your front page always lives at /.'
            : moved
              ? `${fullAddress ? `${fullAddress}. ` : ''}The old address, ${liveAddress}, will redirect here once you publish.`
              : fullAddress || undefined
        }
      >
        <input
          className="ink-site-input pe-mono"
          value={fields.address}
          disabled={isHome}
          onChange={(e) => onPage({ address: e.target.value })}
        />
      </Field>
      <Field
        label="Search and share description"
        note={`${fields.description.length} of ${DESCRIPTION_MAX} characters. Shown in search results and link previews.`}
      >
        <textarea
          className="ink-site-input pe-textarea"
          data-ce-field="description"
          rows={3}
          maxLength={DESCRIPTION_MAX}
          value={fields.description}
          onChange={(e) => onPage({ description: e.target.value })}
        />
      </Field>

      <div className="pe-section">BLOCKS · {fields.blocks.length}</div>
      {blockRows(fields.blocks.length, removed).map((row) => {
        if (row.kind === 'removed') {
          const { key, block } = row.removed;
          const name = blockLabel(block);
          return (
            <div className="pe-removed" role="status" key={key}>
              <div className="pe-removed-text">
                <span className="pe-removed-name">Removed {name}</span>
                <span className="pe-note">
                  {removedNote(
                    fromAgent(block) || Boolean(block._rich) || !ADDABLE.has(block.type)
                  )}
                </span>
              </div>
              <button
                type="button"
                className="ink-btn"
                data-undo-k={key}
                aria-label={`Undo removing ${name}`}
                onClick={() => onUndo(key)}
              >
                Undo
              </button>
            </div>
          );
        }
        const { i } = row;
        const b = fields.blocks[i];
        // "Features · How it works": which block this is, in the card and in
        // every button's name (Round 34 B, backlog f00d07bd).
        const label = blockLabel(b);
        return (
          <div
            key={`b${i}`}
            className="pe-block"
            data-ce-block={i}
            data-picked={picked === i ? 'true' : undefined}
            onClick={() => picked !== i && onPick(i)}
            // Focusing a field picks its block too: Tab, or a field reached
            // without a click (5251c4e2).
            onFocus={() => picked !== i && onPick(i)}
            // A new block's problem shows once its field has been left (blockEdits).
            onBlur={(e) => {
              const ceKey = (e.target as HTMLElement).dataset?.ceKey;
              if (b._new && ceKey) {
                const next = leftField(b, ceKey);
                if (next !== b)
                  onPage({ blocks: fields.blocks.map((x, j) => (j === i ? next : x)) });
              }
            }}
          >
            <div className="pe-block-head">
              <span className="pe-block-type" title={label}>
                {label}
                {fromAgent(b) ? <span className="pe-agent"> · from your agent</span> : null}
              </span>
              <span className="pe-block-tools">
                <button
                  type="button"
                  aria-label={`Move ${label} up`}
                  disabled={i === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBlocks(move(fields.blocks, i, -1));
                    onPick(i - 1);
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`Move ${label} down`}
                  disabled={i === fields.blocks.length - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBlocks(move(fields.blocks, i, 1));
                    onPick(i + 1);
                  }}
                >
                  ↓
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${label}`}
                  data-block-remove={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(i);
                  }}
                >
                  ✕
                </button>
              </span>
            </div>
            <BlockFields
              block={b}
              problems={problems.shownBlocks[i] || {}}
              forms={forms}
              destinations={destinations}
              prompt={blockPrompt(
                { title: fields.title, path: fields.address },
                b,
                i + 1,
                fields.blocks.length
              )}
              onChange={(patch) => onBlock(i, patch)}
            />
          </div>
        );
      })}

      <div className="pe-section">ADD A BLOCK</div>
      <div className="pe-add">
        {NEW_BLOCKS.map((n) => (
          <button
            key={n.label}
            type="button"
            className="ink-chip"
            disabled={n.label === 'Form' && !forms.length}
            title={n.label === 'Form' && !forms.length ? 'Make a form in Forms first' : undefined}
            onClick={() => {
              setBlocks([...fields.blocks, { ...n.make(forms[0]?.id || ''), _new: true }]);
              onPick(fields.blocks.length);
            }}
          >
            + {n.label}
          </button>
        ))}
      </div>
    </>
  );
}

function BlockFields({
  block: b,
  problems,
  forms,
  destinations,
  prompt,
  onChange,
}: {
  block: Block;
  problems: Record<string, string>;
  forms: Array<{ id: string; name: string }>;
  destinations: Array<{ group: string; items: Array<{ href: string; label: string }> }>;
  /** What Ask your agent copies, for a block edited only by the agent. */
  prompt: string;
  onChange: (patch: Partial<Block>) => void;
}) {
  const text = (
    key: string,
    label: string,
    opts: { area?: boolean; problem?: string; note?: string } = {}
  ) => (
    <Field label={label} problem={opts.problem ?? problems[key]} note={opts.note}>
      {opts.area ? (
        <textarea
          className="ink-site-input pe-textarea"
          data-ce-key={key}
          rows={4}
          value={b[key] || ''}
          onChange={(e) => onChange({ [key]: e.target.value })}
        />
      ) : (
        <input
          className="ink-site-input"
          data-ce-key={key}
          value={b[key] || ''}
          onChange={(e) => onChange({ [key]: e.target.value })}
        />
      )}
    </Field>
  );
  // Every block's heading takes *stars* for italics (the site's emphasis).
  const heading = (
    <HeadingField
      value={b.heading || ''}
      problem={problems.heading}
      onChange={(v) => onChange({ heading: v })}
    />
  );
  switch (b.type) {
    case 'hero':
      return (
        <>
          {heading}
          {text('text', 'Subheading', { area: true })}
        </>
      );
    case 'articles':
      return (
        <>
          {heading}
          <p className="pe-note">
            The articles are listed for you. Your agent keeps this list current.
          </p>
        </>
      );
    case 'text':
      return (
        <>
          {heading}
          {b._rich ? (
            <p className="pe-note">
              This text has lists or pictures the editor can’t show. Ask your agent to change it.
            </p>
          ) : (
            text('body', 'Text', {
              area: true,
              note: '**Bold**, *italics* and [links](https://…) work here.',
            })
          )}
        </>
      );
    case 'quote':
      return (
        <>
          {text('text', 'Quote', { area: true })}
          {text('attribution', 'Who said it')}
        </>
      );
    case 'cta':
      return (
        <>
          {heading}
          <Field label="Button label" problem={problems.label}>
            <input
              className="ink-site-input"
              data-ce-key="button.label"
              value={b.button?.label || ''}
              onChange={(e) => onChange({ button: { ...(b.button || {}), label: e.target.value } })}
            />
          </Field>
          <Field label="Button goes to">
            <GoesTo
              label="Button goes to"
              value={b.button?.href || ''}
              destinations={destinations}
              onChange={(href) => onChange({ button: { ...(b.button || {}), href } })}
            />
          </Field>
        </>
      );
    case 'form':
      return (
        <>
          <Field label="Form" problem={problems.formId}>
            <select
              className="ink-site-input pe-select"
              value={b.formId || ''}
              onChange={(e) => onChange({ formId: e.target.value })}
            >
              {!b.formId ? <option value="">Pick a form</option> : null}
              {forms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
          <p className="pe-note">
            Fields and small print are set in Forms.{' '}
            {b.formId ? (
              <Link to={`/content?view=forms&form=${encodeURIComponent(b.formId)}`}>
                Edit fields in Forms →
              </Link>
            ) : null}
          </p>
        </>
      );
    default:
      return <AgentEditsBlock prompt={prompt} />;
  }
}

function PartFieldsForm({
  part,
  fields,
  problems,
  destinations,
  mega = false,
  tagline = '',
  askInMenu = false,
  onPart,
}: {
  part: 'header' | 'footer';
  fields: PartFields;
  problems: PartProblems;
  destinations: Array<{ group: string; items: Array<{ href: string; label: string }> }>;
  /** The header's dropdowns open as a mega panel, where links show a description. */
  mega?: boolean;
  /** The site's tagline (edited in Site), for the Tagline in header note. */
  tagline?: string;
  /** Reader questions use the Ask page: the site adds "Ask AI" to the menu. */
  askInMenu?: boolean;
  onPart: (patch: Partial<PartFields>) => void;
}) {
  const setColumn = (i: number, patch: Partial<FooterColumn>) =>
    onPart({ columns: fields.columns.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const columnLinks = fields.columns.reduce((n, c) => n + c.links.length, 0);
  const dupes =
    part === 'footer' ? footerDuplicates(fields.links, fields.columns) : { links: {}, columns: {} };
  return (
    <>
      {part === 'header' ? (
        <Field
          label="Site name"
          problem={problems.siteName}
          note="Shown at the top of every page and in the browser tab."
        >
          <input
            className="ink-site-input"
            value={fields.siteName}
            onChange={(e) => onPart({ siteName: e.target.value })}
          />
        </Field>
      ) : null}
      {/* The editor has no leave guard: links out of it open in a new tab, so
          unsaved edits stay. */}
      {part === 'header' ? (
        <SwitchRow
          label="Tagline in header"
          on={fields.showTagline}
          onChange={(showTagline) => onPart({ showTagline })}
          note={
            <>
              {tagline.trim()
                ? `${fields.showTagline ? 'Shown' : 'Hidden'}. Your tagline: “${tagline.trim()}”. `
                : 'Your site has no tagline yet. '}
              <Link to="/content?view=manage&area=site#identity" target="_blank" rel="noreferrer">
                Edit in Site →
              </Link>
            </>
          }
        />
      ) : (
        <Field
          label="Footer text"
          note="Your sign-in email is never added here. To show an address, set a contact email in Site."
        >
          <textarea
            className="ink-site-input pe-textarea"
            rows={2}
            value={fields.footerText}
            onChange={(e) => onPart({ footerText: e.target.value })}
          />
        </Field>
      )}

      {/* The footer as readers see it: the columns, then the bottom row (97fed8af). */}
      {part === 'footer' ? (
        <>
          <div className="pe-section">
            COLUMNS · {fields.columns.length} · {columnLinks} {columnLinks === 1 ? 'LINK' : 'LINKS'}
          </div>
          {fields.columns.map((c, i) => (
            <div key={i} className="pe-column">
              <div className="pe-link-row">
                <span className="pe-column-label">Column {i + 1} heading</span>
                <span className="pe-block-tools">
                  <button
                    type="button"
                    aria-label="Move column up"
                    disabled={i === 0}
                    onClick={() => onPart({ columns: move(fields.columns, i, -1) })}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label="Move column down"
                    disabled={i === fields.columns.length - 1}
                    onClick={() => onPart({ columns: move(fields.columns, i, 1) })}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    aria-label="Remove column"
                    onClick={() => onPart({ columns: fields.columns.filter((_, j) => j !== i) })}
                  >
                    ✕
                  </button>
                </span>
              </div>
              <input
                className="ink-site-input"
                aria-label={`Column ${i + 1} heading`}
                placeholder="Heading (optional)"
                maxLength={40}
                value={c.heading}
                onChange={(e) => setColumn(i, { heading: e.target.value })}
              />
              <LinkList
                links={c.links}
                problems={problems.columns[i]?.links || {}}
                warnings={dupes.columns[i]}
                destinations={destinations}
                max={MAX_COLUMN_LINKS}
                addLabel={c.heading.trim() ? `+ Add link to ${c.heading.trim()}` : '+ Add link'}
                onLinks={(links) => setColumn(i, { links })}
              />
              {problems.columns[i]?.empty ? (
                <span className="pe-problem">{problems.columns[i].empty}</span>
              ) : null}
            </div>
          ))}
          {fields.columns.length < MAX_COLUMNS ? (
            <button
              type="button"
              className="ink-btn pe-add-link"
              onClick={() =>
                onPart({
                  columns: [...fields.columns, { heading: '', links: [{ label: '', href: '/' }] }],
                })
              }
            >
              + Add column
            </button>
          ) : null}
          <p className="pe-note">Up to {MAX_COLUMNS} columns. Columns sit above the bottom row.</p>
        </>
      ) : null}

      <div className="pe-section">
        {part === 'header'
          ? `MENU LINKS · ${fields.links.length}`
          : `BOTTOM ROW · ${fields.links.length} ${fields.links.length === 1 ? 'LINK' : 'LINKS'}`}
      </div>
      <LinkList
        links={fields.links}
        problems={problems.links}
        warnings={dupes.links}
        destinations={destinations}
        menus={part === 'header' ? { problems: problems.children, mega } : undefined}
        onLinks={(links) => onPart({ links })}
      />

      {part === 'header' ? (
        <>
          <div className="pe-section">BUTTON</div>
          <div className="pe-link">
            <SwitchRow
              label="Show a button"
              on={fields.button.on}
              onChange={(on) => onPart({ button: { ...fields.button, on } })}
              note="An accent button at the right end of the menu."
            />
            {fields.button.on ? (
              <>
                <input
                  className="ink-site-input"
                  aria-label="Button label"
                  placeholder="Button label"
                  maxLength={30}
                  value={fields.button.label}
                  onChange={(e) => onPart({ button: { ...fields.button, label: e.target.value } })}
                />
                <GoesTo
                  value={fields.button.href}
                  destinations={destinations}
                  onChange={(href) => onPart({ button: { ...fields.button, href } })}
                />
              </>
            ) : null}
            {problems.button ? <span className="pe-problem">{problems.button}</span> : null}
          </div>
          {askInMenu && !fields.links.some((l) => /^\/ask\/?$/.test(l.href)) ? (
            <>
              <div className="pe-section">ADDED AUTOMATICALLY</div>
              <div className="pe-auto-link">
                <span className="pe-auto-link-row">
                  <strong>Ask AI</strong> · <span className="pe-mono">/ask</span> · Last in the menu
                </span>
                <span className="pe-note">
                  Shown because reader questions use the Ask page.{' '}
                  <Link to="/prism-ai/settings#reader-questions" target="_blank" rel="noreferrer">
                    Change in Reader questions →
                  </Link>
                </span>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/**
 * Links to edit in place: a label, where it goes, move up/down and remove.
 * menus (the header): each link can hold a dropdown of its own links, edited
 * the same way, with a short description each when the menu is a mega panel.
 */
function LinkList({
  links,
  problems,
  warnings,
  destinations,
  max,
  addLabel = '+ Add link',
  menus,
  describe = false,
  onLinks,
}: {
  links: NavLinkRow[];
  problems: Record<number, string>;
  /** Amber notes that don't block a save (the footer's duplicate links). */
  warnings?: Record<number, string>;
  destinations: Array<{ group: string; items: Array<{ href: string; label: string }> }>;
  max?: number;
  addLabel?: string;
  menus?: { problems: Record<number, Record<number, string>>; mega: boolean };
  /** A description line under each label (mega menu links). */
  describe?: boolean;
  onLinks: (links: NavLinkRow[]) => void;
}) {
  const setLink = (i: number, patch: Partial<NavLinkRow>) =>
    onLinks(links.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <>
      {links.map((l, i) => (
        <div key={i} className="pe-link">
          <div className="pe-link-row">
            <input
              className="ink-site-input"
              aria-label="Link label"
              placeholder="Label"
              value={l.label}
              onChange={(e) => setLink(i, { label: e.target.value })}
            />
            <span className="pe-block-tools">
              <button
                type="button"
                aria-label="Move up"
                disabled={i === 0}
                onClick={() => onLinks(move(links, i, -1))}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label="Move down"
                disabled={i === links.length - 1}
                onClick={() => onLinks(move(links, i, 1))}
              >
                ↓
              </button>
              <button
                type="button"
                aria-label="Remove link"
                onClick={() => onLinks(links.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </span>
          </div>
          {describe ? (
            <input
              className="ink-site-input"
              aria-label="Link description"
              placeholder="Short description (optional)"
              maxLength={120}
              value={l.description || ''}
              onChange={(e) => setLink(i, { description: e.target.value })}
            />
          ) : null}
          <GoesTo
            value={l.href}
            destinations={destinations}
            parent={Array.isArray(l.children) && l.children.length > 0}
            onChange={(href) => setLink(i, { href })}
          />
          {problems[i] ? <span className="pe-problem">{problems[i]}</span> : null}
          {warnings?.[i] ? <span className="pe-warning">{warnings[i]}</span> : null}
          {menus ? (
            Array.isArray(l.children) && l.children.length ? (
              <div className="pe-sublinks">
                <div className="pe-section">
                  MENU UNDER IT · {l.children.length} {l.children.length === 1 ? 'LINK' : 'LINKS'}
                </div>
                <LinkList
                  links={l.children}
                  problems={menus.problems[i] || {}}
                  destinations={destinations}
                  max={MAX_MENU_LINKS}
                  describe={menus.mega}
                  addLabel={l.label.trim() ? `+ Add link under ${l.label.trim()}` : '+ Add link'}
                  onLinks={(children) =>
                    setLink(i, { children: children.length ? children : undefined })
                  }
                />
              </div>
            ) : (
              <button
                type="button"
                className="ink-btn pe-add-link"
                onClick={() => setLink(i, { children: [{ label: '', href: '/' }] })}
              >
                + Add a menu under it
              </button>
            )
          ) : Array.isArray(l.children) && l.children.length ? (
            <span className="pe-note">
              {l.children.length} more {l.children.length === 1 ? 'link' : 'links'} under it, kept
              as they are.
            </span>
          ) : null}
        </div>
      ))}
      {max === undefined || links.length < max ? (
        <button
          type="button"
          className="ink-btn pe-add-link"
          onClick={() => onLinks([...links, { label: '', href: '/' }])}
        >
          {addLabel}
        </button>
      ) : null}
    </>
  );
}

/**
 * The page's status line, kept while editing: "● Live · site.tinycms.ai/about ↗
 * · 4 blocks · updated 2 days ago"; the header and footer: "● Live · On every
 * page". blocks is the count as edited.
 */
function EditingMeta({
  row,
  isHome,
  blocks,
  siteUrl,
  partPending,
}: {
  row: PageRowLite | null;
  isHome: boolean;
  blocks: number | null;
  siteUrl: string | null;
  partPending: boolean | null;
}) {
  if (partPending !== null) {
    return (
      <div className="pg-meta">
        <span
          className="pg-status"
          style={{ color: partPending ? 'var(--ink-amber)' : 'var(--ink-green-ink)' }}
        >
          <span
            className="pg-dot"
            style={{ background: partPending ? 'var(--ink-amber)' : 'var(--ink-green)' }}
          />
          {partPending ? 'Changes pending' : 'Live'}
        </span>
        <span>·</span>
        <span>On every page</span>
      </div>
    );
  }
  if (!row?.state) return null;
  const st = pageStatus(row.state);
  const live = Boolean(row.state.live);
  const path = isHome ? '/' : row.path;
  const n = blocks ?? row.blockCount;
  return (
    <div className="pg-meta">
      <span className="pg-status" style={{ color: st.color }}>
        <span className="pg-dot" style={{ background: st.dot }} />
        {st.label}
      </span>
      <span>·</span>
      {live && siteUrl ? (
        <a
          href={`${siteUrl}${path === '/' ? '' : path}`}
          target="_blank"
          rel="noreferrer"
          className="pg-url"
        >
          {siteUrl.replace(/^https?:\/\//, '')}
          {path === '/' ? '' : path} ↗
        </a>
      ) : (
        <span className="pg-url" data-muted="true">
          {path}
        </span>
      )}
      {typeof n === 'number' ? (
        <>
          <span>·</span>
          <span>
            {n} block{n === 1 ? '' : 's'}
            {row.updatedAt ? ` · ${ago(row.updatedAt)}` : ''}
          </span>
        </>
      ) : null}
    </div>
  );
}
